// ==============================================================
// src/components/EmployeeRegistrationPage.jsx
// Formulario público para que nuevos empleados se registren solos
// ==============================================================
import React, { useState, useRef } from 'react';
import {
  Box, Paper, Stepper, Step, StepLabel, Button, Typography,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Divider, Grid, InputAdornment,
  Chip, Avatar, Checkbox, FormControlLabel, FormGroup
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ScheduleIcon from '@mui/icons-material/Schedule';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
const LOGO_URL = 'https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM-1.jpeg';

const STEPS = ['Datos personales', 'Salud & Seguridad', 'Habilidades & Disponibilidad', 'Pago & Emergencia'];

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const SKILLS_OPTIONS = [
  { key: 'conductor', label: 'Conductor (vehículos)' },
  { key: 'soldador', label: 'Soldador' },
  { key: 'globoflexia', label: 'Globoflexia' },
  { key: 'animador', label: 'Animador / Recreacionista' },
  { key: 'pintucaritas', label: 'Pintucaritas' },
  { key: 'operario_inflable', label: 'Operario de inflables' },
  { key: 'operario_mecanico', label: 'Operario juegos mecánicos' },
  { key: 'electricista', label: 'Electricista' },
  { key: 'sonido', label: 'Sonido / DJ' },
  { key: 'fotografia', label: 'Fotografía / Video' },
  { key: 'cocina', label: 'Cocina / Alimentos' },
  { key: 'primeros_auxilios', label: 'Primeros auxilios' },
];

const TRANSPORT_OPTIONS = [
  { key: 'ninguno', label: 'Ninguno (transporte público)' },
  { key: 'bicicleta', label: 'Bicicleta' },
  { key: 'moto', label: 'Moto' },
  { key: 'carro', label: 'Carro' },
];

const EMPTY = {
  // Paso 1 — Datos personales
  firstName: '', lastName: '',
  documentType: 'CC', documentNumber: '',
  phone: '', whatsapp: '',
  email: '', address: '', city: '', neighborhood: '',
  birthDate: '',
  // Paso 2 — Salud & Seguridad Social
  bloodType: '',
  eps: { name: '', code: '' },
  arl: { name: '', code: '' },
  pensionFund: { name: '', code: '' },
  compensationFund: { name: '', code: '' },
  medicalInfo: { allergies: '', medications: '', disabilities: '', notes: '' },
  // Paso 3 — Habilidades & Disponibilidad
  skills: [],
  skillsOther: '',
  talleres: '',
  transport: 'ninguno',
  licencia: '',
  availability: { days: [], horaInicio: '07:00', horaFin: '18:00', notas: '' },
  // Paso 4 — Pago & Contacto emergencia
  paymentMethod: 'nequi',
  paymentDetails: { nequiNumber: '', daviplataNumber: '', bankName: '', bankAccountType: '', bankAccountNumber: '', bankAccountHolder: '' },
  emergencyContact: { name: '', phone: '', relationship: '' },
  notes: '',
};

export default function EmployeeRegistrationPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [serverError, setServerError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileRef = useRef();

  const upd = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const updNested = (parent, field, value) =>
    setForm(p => ({ ...p, [parent]: { ...p[parent], [field]: value } }));

  const toggleSkill = (key) => {
    setForm(p => ({
      ...p,
      skills: p.skills.includes(key) ? p.skills.filter(s => s !== key) : [...p.skills, key]
    }));
  };

  const toggleDay = (day) => {
    setForm(p => ({
      ...p,
      availability: {
        ...p.availability,
        days: p.availability.days.includes(day)
          ? p.availability.days.filter(d => d !== day)
          : [...p.availability.days, day]
      }
    }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setServerError('La foto no puede superar 5MB');
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setServerError('');
    }
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.firstName.trim()) e.firstName = 'Requerido';
      if (!form.lastName.trim()) e.lastName = 'Requerido';
      if (!form.documentNumber.trim()) e.documentNumber = 'Requerido';
      if (!form.phone.trim()) e.phone = 'Requerido';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setServerError('');
    try {
      // Enviar datos del formulario
      const res = await fetch(`${API_BASE}/staff/registro-publico`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Error al registrar');

      // Si hay foto, subirla al empleado creado
      if (photoFile && data.employeeId) {
        try {
          const fd = new FormData();
          fd.append('photo', photoFile);
          await fetch(`${API_BASE}/staff/employees/${data.employeeId}/photo`, { method: 'POST', body: fd });
        } catch (_) { /* foto es opcional, no bloquear */ }
      }

      setDone({ code: data.code, nombre: data.nombre });
    } catch (err) {
      setServerError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de éxito
  if (done) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper elevation={4} sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center', borderRadius: 3 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#10b981', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>¡Registro exitoso!</Typography>
          <Typography color="text.secondary" gutterBottom>
            Gracias <strong>{done.nombre}</strong>, tus datos fueron enviados correctamente.
          </Typography>
          <Paper sx={{ bgcolor: '#f0fdf4', p: 2, my: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">Tu código de empleado</Typography>
            <Typography variant="h4" fontWeight={700} color="primary">{done.code}</Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            El equipo de Inflables Da Silva revisará tu información y te contactará pronto.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 650, mx: 'auto' }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={LOGO_URL} alt="Inflables Da Silva" style={{ height: 56, objectFit: 'contain' }} />
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>Formulario de Registro de Empleado</Typography>
          <Typography variant="body2" color="text.secondary">Completa tus datos para unirte al equipo</Typography>
        </Box>

        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
            {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}

          {/* PASO 1: Datos Personales + Foto */}
          {step === 0 && (
            <Grid container spacing={2}>
              {/* Foto selfie */}
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <input type="file" accept="image/*" capture="user" hidden ref={fileRef} onChange={handlePhoto} />
                <Avatar
                  src={photoPreview}
                  sx={{ width: 100, height: 100, mx: 'auto', mb: 1, cursor: 'pointer', bgcolor: '#e2e8f0', border: '3px dashed #94a3b8' }}
                  onClick={() => fileRef.current?.click()}
                >
                  {!photoPreview && <CameraAltIcon sx={{ fontSize: 40, color: '#64748b' }} />}
                </Avatar>
                <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()}>
                  {photoPreview ? 'Cambiar foto' : 'Subir foto (selfie)'}
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Opcional - Toma una selfie o sube una foto tuya
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Nombres" value={form.firstName}
                  onChange={e => upd('firstName', e.target.value)} error={!!errors.firstName}
                  helperText={errors.firstName} InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Apellidos" value={form.lastName}
                  onChange={e => upd('lastName', e.target.value)} error={!!errors.lastName} helperText={errors.lastName} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth><InputLabel>Tipo Doc.</InputLabel>
                  <Select value={form.documentType} label="Tipo Doc." onChange={e => upd('documentType', e.target.value)}>
                    <MenuItem value="CC">Cédula (CC)</MenuItem>
                    <MenuItem value="CE">Cédula Extranjería</MenuItem>
                    <MenuItem value="TI">Tarjeta Identidad</MenuItem>
                    <MenuItem value="PPT">PPT</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth required label="Número de documento" value={form.documentNumber}
                  onChange={e => upd('documentNumber', e.target.value)} error={!!errors.documentNumber} helperText={errors.documentNumber} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Teléfono / Celular" value={form.phone}
                  onChange={e => upd('phone', e.target.value)} error={!!errors.phone} helperText={errors.phone} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="WhatsApp (si es diferente)" value={form.whatsapp}
                  onChange={e => upd('whatsapp', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Correo electrónico" type="email" value={form.email}
                  onChange={e => upd('email', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ciudad" value={form.city} onChange={e => upd('city', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Barrio" value={form.neighborhood} onChange={e => upd('neighborhood', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Dirección de residencia" value={form.address} onChange={e => upd('address', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Fecha de nacimiento" type="date" value={form.birthDate}
                  onChange={e => upd('birthDate', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          )}

          {/* PASO 2: Salud & Seguridad */}
          {step === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalHospitalIcon fontSize="small" /> Información de Salud
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth><InputLabel>Tipo de sangre</InputLabel>
                  <Select value={form.bloodType} label="Tipo de sangre" onChange={e => upd('bloodType', e.target.value)}>
                    {['', 'A+','A-','B+','B-','AB+','AB-','O+','O-'].map(t => <MenuItem key={t} value={t}>{t || 'No sé'}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth label="Alergias conocidas" value={form.medicalInfo.allergies}
                  onChange={e => updNested('medicalInfo', 'allergies', e.target.value)} placeholder="Ej: Polen, mariscos, ninguna" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Medicamentos que toma regularmente" value={form.medicalInfo.medications}
                  onChange={e => updNested('medicalInfo', 'medications', e.target.value)} placeholder="Ej: Ninguno" />
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption">Seguridad Social</Typography></Divider></Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="EPS (nombre)" value={form.eps.name}
                  onChange={e => updNested('eps', 'name', e.target.value)} placeholder="Ej: Sanitas, Sura, Nueva EPS" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="EPS (N° afiliación)" value={form.eps.code}
                  onChange={e => updNested('eps', 'code', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ARL (nombre)" value={form.arl.name}
                  onChange={e => updNested('arl', 'name', e.target.value)} placeholder="Ej: Positiva, Sura ARL, Colmena" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ARL (N° afiliación)" value={form.arl.code}
                  onChange={e => updNested('arl', 'code', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Fondo de pensiones" value={form.pensionFund.name}
                  onChange={e => updNested('pensionFund', 'name', e.target.value)} placeholder="Ej: Porvenir, Protección" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Caja de compensación" value={form.compensationFund.name}
                  onChange={e => updNested('compensationFund', 'name', e.target.value)} placeholder="Ej: Comfenalco, Compensar" />
              </Grid>
            </Grid>
          )}

          {/* PASO 3: Habilidades & Disponibilidad */}
          {step === 2 && (
            <Grid container spacing={2}>
              {/* Habilidades */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BuildIcon fontSize="small" /> Habilidades y experiencia
                </Typography>
                <Typography variant="caption" color="text.secondary">Selecciona todas las que apliquen</Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {SKILLS_OPTIONS.map(s => (
                    <Chip key={s.key} label={s.label} variant={form.skills.includes(s.key) ? 'filled' : 'outlined'}
                      color={form.skills.includes(s.key) ? 'primary' : 'default'}
                      onClick={() => toggleSkill(s.key)} sx={{ cursor: 'pointer' }} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Talleres que maneja" value={form.talleres}
                  onChange={e => upd('talleres', e.target.value)} placeholder="Ej: Slime, pintura, manualidades..." multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Otras habilidades" value={form.skillsOther}
                  onChange={e => upd('skillsOther', e.target.value)} placeholder="Escribe otras habilidades..." multiline rows={2} />
              </Grid>

              {/* Transporte */}
              <Grid item xs={12}><Divider><Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><DirectionsCarIcon fontSize="small" /> Medio de transporte</Typography></Divider></Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth><InputLabel>Transporte</InputLabel>
                  <Select value={form.transport} label="Transporte" onChange={e => upd('transport', e.target.value)}>
                    {TRANSPORT_OPTIONS.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {(form.transport === 'moto' || form.transport === 'carro') && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Tipo de licencia" value={form.licencia}
                    onChange={e => upd('licencia', e.target.value)} placeholder="Ej: A2, B1, C1" />
                </Grid>
              )}

              {/* Disponibilidad */}
              <Grid item xs={12}><Divider><Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ScheduleIcon fontSize="small" /> Disponibilidad</Typography></Divider></Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Días que puedes trabajar</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {DAYS.map(day => (
                    <Chip key={day} label={day} variant={form.availability.days.includes(day) ? 'filled' : 'outlined'}
                      color={form.availability.days.includes(day) ? 'success' : 'default'}
                      onClick={() => toggleDay(day)} sx={{ cursor: 'pointer' }} />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Hora inicio" type="time" value={form.availability.horaInicio}
                  onChange={e => updNested('availability', 'horaInicio', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Hora fin" type="time" value={form.availability.horaFin}
                  onChange={e => updNested('availability', 'horaFin', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Notas de disponibilidad" value={form.availability.notas}
                  onChange={e => updNested('availability', 'notas', e.target.value)} placeholder="Ej: Solo fines de semana, no festivos..." />
              </Grid>
            </Grid>
          )}

          {/* PASO 4: Pago & Emergencia */}
          {step === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PaymentsIcon fontSize="small" /> Método de pago preferido
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth><InputLabel>Método de pago</InputLabel>
                  <Select value={form.paymentMethod} label="Método de pago" onChange={e => upd('paymentMethod', e.target.value)}>
                    <MenuItem value="nequi">Nequi</MenuItem>
                    <MenuItem value="daviplata">Daviplata</MenuItem>
                    <MenuItem value="efectivo">Efectivo</MenuItem>
                    <MenuItem value="transferencia">Transferencia bancaria</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {form.paymentMethod === 'nequi' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Número Nequi" value={form.paymentDetails.nequiNumber}
                    onChange={e => updNested('paymentDetails', 'nequiNumber', e.target.value)} />
                </Grid>
              )}
              {form.paymentMethod === 'daviplata' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Número Daviplata" value={form.paymentDetails.daviplataNumber}
                    onChange={e => updNested('paymentDetails', 'daviplataNumber', e.target.value)} />
                </Grid>
              )}
              {form.paymentMethod === 'transferencia' && (<>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Banco" value={form.paymentDetails.bankName} onChange={e => updNested('paymentDetails', 'bankName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth><InputLabel>Tipo cuenta</InputLabel>
                    <Select value={form.paymentDetails.bankAccountType} label="Tipo cuenta" onChange={e => updNested('paymentDetails', 'bankAccountType', e.target.value)}>
                      <MenuItem value="ahorros">Ahorros</MenuItem>
                      <MenuItem value="corriente">Corriente</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="N° de cuenta" value={form.paymentDetails.bankAccountNumber} onChange={e => updNested('paymentDetails', 'bankAccountNumber', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Titular de la cuenta" value={form.paymentDetails.bankAccountHolder} onChange={e => updNested('paymentDetails', 'bankAccountHolder', e.target.value)} /></Grid>
              </>)}

              <Grid item xs={12}><Divider><Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><ContactEmergencyIcon fontSize="small" /> Contacto de emergencia</Typography></Divider></Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth label="Nombre completo" value={form.emergencyContact.name}
                  onChange={e => updNested('emergencyContact', 'name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Teléfono" value={form.emergencyContact.phone}
                  onChange={e => updNested('emergencyContact', 'phone', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Parentesco" value={form.emergencyContact.relationship}
                  onChange={e => updNested('emergencyContact', 'relationship', e.target.value)} placeholder="Mamá, Esposo..." />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Notas adicionales (opcional)" value={form.notes}
                  onChange={e => upd('notes', e.target.value)} placeholder="Algo importante que debamos saber" />
              </Grid>
            </Grid>
          )}

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button variant="outlined" onClick={back} disabled={step === 0}>Atrás</Button>
            {step < STEPS.length - 1
              ? <Button variant="contained" onClick={next}>Siguiente</Button>
              : <Button variant="contained" color="success" onClick={handleSubmit} disabled={loading} sx={{ minWidth: 140 }}>
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar registro'}
                </Button>
            }
          </Box>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          Inflables Da Silva & FunPark © {new Date().getFullYear()} — Tus datos son confidenciales
        </Typography>
      </Box>
    </Box>
  );
}
